import React, { useState } from "react";
import { T } from "../../app/tokens.js";
import { OkrStatusBadge } from "./OkrStatusBadge.jsx";
import { AlertCircle, Edit2 } from "lucide-react";
import { OkrInitiativeUpdatesModal } from "./OkrInitiativeUpdatesModal.jsx";
import { downloadOkrsExecStatus } from "../../data/okrsClient.js";

export function OkrDigestView({ data, currentMonth, onMonthChange, year, loading, currentUser, currentRole, onSaveUpdate }) {
  const [updateModal, setUpdateModal] = useState({ open: false, initiative: null, initialMonth: null });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const canEditBase = currentRole === "admin" || currentRole === "editor";
  const exportPpt = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadOkrsExecStatus(year, currentMonth);
    } catch (error) {
      setExportError(error.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h2 style={{ fontSize: T.fsLg, fontWeight: T.fwSemibold, margin: "0 0 4px", color: T.dark }}>Monthly Digest</h2>
          <p style={{ margin: 0, fontSize: T.fsSm, color: T.mid }}>Objective and key result rollup narratives.</p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <input
          type="month" 
          value={currentMonth} 
           min={`${year}-01`}
           max={`${year}-12`}
          onChange={(e) => onMonthChange(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${T.borderDark}`, borderRadius: 6, fontSize: T.fsBase, color: T.dark, background: T.white }}
        />
        {canEditBase && <button type="button" onClick={exportPpt} disabled={exporting || loading || !data}
          style={{ padding: "8px 14px", border: 0, borderRadius: 6, background: T.dark, color: T.white, cursor: "pointer", fontWeight: T.fwSemibold }}>
          {exporting ? "Exporting..." : "Export to PPT"}
        </button>}
        </div>
      </div>
      {exportError && <div role="alert" style={{ color: T.red }}>{exportError}</div>}

      {!data ? <div style={{ padding: 40, color: T.mid }}>{loading ? "Loading monthly digest..." : "No digest available."}</div> : data.objectives?.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", background: T.card, borderRadius: 8, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: T.fsMd5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 8 }}>No Objectives Found</div>
          <div style={{ fontSize: T.fsBase, color: T.mid }}>There are no objectives for this period.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {data.objectives?.map(obj => (
            <div key={obj.id} style={{ background: T.white, borderRadius: 8, border: `1px solid ${T.border}`, overflow: "hidden", boxShadow: T.shadowTileXs }}>
              <div style={{ padding: "16px 20px", background: T.dark, color: T.white }}>
                <h3 style={{ margin: 0, fontSize: T.fsMd5, fontWeight: T.fwSemibold }}>{obj.name}</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {obj.keyResults?.map(kr => (
                  <div key={kr.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ padding: "12px 20px", background: T.bg, fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, borderBottom: `1px solid ${T.border}` }}>
                      {kr.title}
                    </div>
                    <div>
                      {(!kr.initiatives || kr.initiatives.length === 0) ? (
                        <div style={{ padding: "16px 20px", fontSize: T.fsSm, color: T.mid }}>No initiatives linked to this key result.</div>
                      ) : kr.initiatives.map(ini => {
                        const canEdit = canEditBase || ini.ownerId === currentUser?.id;
                        return (
                          <div key={ini.id} style={{ padding: "20px", display: "flex", gap: 24, borderBottom: `1px solid ${T.borderCompact}`, background: T.white }}>
                            <div style={{ width: 280, flexShrink: 0 }}>
                              <div style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 8 }}>{ini.title}</div>
                              <div style={{ fontSize: T.fsXs, color: T.mid, display: "flex", flexDirection: "column", gap: 4 }}>
                                <div><strong style={{ fontWeight: T.fwSemibold }}>Owner:</strong> {ini.owner || "Unassigned"}</div>
                                 {ini.project && <div><strong style={{ fontWeight: T.fwSemibold }}>Project:</strong> {ini.project.name || "Linked project"}</div>}
                              </div>
                            </div>
                            
                            <div style={{ flex: 1 }}>
                              {ini.update ? (
                                <div style={{ background: T.bg, padding: 16, borderRadius: 8, border: `1px solid ${T.border}` }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                                     <div>
                                       <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 6 }}>Update from {ini.update.month}</div>
                                       {ini.update.status && <OkrStatusBadge status={ini.update.status} />}
                                     </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                      {ini.update.percent != null && (
                                        <span style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark }}>{ini.update.percent}%</span>
                                      )}
                                      {canEdit && (
                                        <button 
                                          onClick={() => setUpdateModal({ open: true, initiative: ini, initialMonth: currentMonth })}
                                          style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: T.blue, fontSize: T.fsXs, cursor: "pointer", padding: 4 }}
                                        >
                                          <Edit2 size={14} /> Edit
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div style={{ fontSize: T.fsSm, color: T.dark, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                                    {ini.update.text}
                                  </div>
                                   {ini.update.nextSteps && <div style={{ marginTop: 10, fontSize: T.fsSm, color: T.dark, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                                     <strong>Next steps:</strong> {ini.update.nextSteps}
                                   </div>}
                                   {ini.missingThisMonth && <div style={{ marginTop: 10, fontSize: T.fsSm, color: T.red }}>No update this month</div>}
                                </div>
                              ) : (
                                <div style={{ background: ini.missingThisMonth ? T.redSoft : T.bg, padding: 16, borderRadius: 8, border: `1px dashed ${ini.missingThisMonth ? T.redMuted : T.borderDark}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: ini.missingThisMonth ? T.red : T.mid, fontSize: T.fsSm }}>
                                    <AlertCircle size={16} /> 
                                     No update this month — no earlier update
                                  </div>
                                  {canEdit && (
                                    <button 
                                      onClick={() => setUpdateModal({ open: true, initiative: ini, initialMonth: currentMonth })}
                                      style={{ background: T.white, border: `1px solid ${T.borderDark}`, padding: "6px 12px", borderRadius: 4, fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, cursor: "pointer" }}
                                    >
                                      Add Update
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {updateModal.open && updateModal.initiative && (
        <OkrInitiativeUpdatesModal 
          isOpen={true}
          onClose={() => setUpdateModal({ open: false, initiative: null, initialMonth: null })}
          initiative={updateModal.initiative}
          initialMonth={updateModal.initialMonth}
          selectedYear={year}
          currentUser={currentUser}
          currentRole={currentRole}
          onSave={async (id, month, payload) => {
            await onSaveUpdate(id, month, payload);
            setUpdateModal({ open: false, initiative: null, initialMonth: null });
          }}
        />
      )}
    </div>
  );
}
